import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Sparkles,
  DollarSign,
  Globe,
  ShieldCheck,
  Clock,
  Send,
  Filter,
  TrendingUp,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { JOBS } from "@/data/mockData";
import FreelanceFinderApi from "../components/common/FreelanceFinderApi";

const SOURCES = ["All", "Upwork", "LinkedIn"];
const TYPES = ["All", "Fixed", "Hourly", "Retainer"];

export default function FreelanceFinder() {
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("All");
  const [type, setType] = useState("All");
  const [selected, setSelected] = useState(null);

  const jobs = useMemo(
    () =>
      JOBS.filter(
        (j) =>
          (src === "All" || j.source === src) &&
          (type === "All" || j.type === type) &&
          (q === "" ||
            j.title.toLowerCase().includes(q.toLowerCase()) ||
            j.client.toLowerCase().includes(q.toLowerCase()) ||
            j.tags.some((t) => t.toLowerCase().includes(q.toLowerCase())))
      ),
    [q, src, type]
  );

  return (
    <div data-testid="freelance-page">
      <FreelanceFinderApi />
      <PageHeader
        eyebrow="Module 01 · Freelance Opportunity Finder"
        title="Upwork & LinkedIn, decoded by AI."
        description="A live feed of every relevant Shopify, ERP, AI, mobile, UI/UX, SEO and marketing job — filtered, scored and matched against Base2Brand's win-rate model."
        actions={
          <Button data-testid="freelance-generate-btn" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate proposals
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard testId="freelance-stat-today"  label="New Jobs Today"  value="128" delta="+18%" accent="indigo" />
        <StatCard testId="freelance-stat-hv"     label="High-Value"      value="34"  delta="+9%"  accent="cyan"   icon={DollarSign} />
        <StatCard testId="freelance-stat-win"    label="Win Rate"        value="32"  suffix="%"   delta="+4.2pt" accent="emerald" />
        <StatCard testId="freelance-stat-rev"    label="Est. Revenue"    value="486k" prefix="$"  delta="+22%"   accent="amber" />
        <StatCard testId="freelance-stat-match"  label="AI Match Avg"    value="87"  suffix="%"   delta="+3.1pt" accent="pink" icon={TrendingUp} />
      </div>

      {/* Filter bar */}
      <div className="glass-card p-3 flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3">
          <Search className="h-4 w-4 text-white/40" />
          <Input
            data-testid="freelance-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search job title, client, keyword…"
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-10 text-sm"
          />
        </div>
        <PillFilter label="Source" value={src} options={SOURCES} onChange={setSrc} testId="freelance-source-filter" />
        <PillFilter label="Type"   value={type} options={TYPES}   onChange={setType} testId="freelance-type-filter" />
        <Button variant="ghost" size="sm" className="text-white/60 rounded-full">
          <Filter className="h-3.5 w-3.5 mr-1.5" />
          More filters
        </Button>
      </div>

      {/* Job list */}
      <div className="grid grid-cols-1 gap-3">
        {jobs.map((job, i) => (
          <JobCard key={job.id} job={job} index={i} onOpen={() => setSelected(job)} />
        ))}
        {jobs.length === 0 && (
          <div className="glass-card p-10 text-center text-white/50">No jobs match your filters.</div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="bg-[#0d0d10] border-l border-white/[0.08] w-[520px] sm:max-w-[520px]" data-testid="freelance-detail-sheet">
          <SheetHeader className={selected ? "" : "sr-only"}>
            <SheetTitle className="font-display text-2xl text-white leading-tight">
              {selected ? selected.title : "Job details"}
            </SheetTitle>
          </SheetHeader>
          {selected && (
            <>
              <div className="flex items-center gap-2 mt-1 text-sm text-white/50">
                {selected.source} · {selected.client} · {selected.country}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniStat label="Budget"    value={selected.budget} icon={DollarSign} />
                <MiniStat label="Match"     value={`${selected.match}%`} icon={Sparkles} />
                <MiniStat label="Win Prob." value={`${selected.winProb}%`} icon={TrendingUp} />
                <MiniStat label="Verified"  value={selected.verified ? "Yes" : "No"} icon={ShieldCheck} />
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-2">Client History</div>
                <div className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-sm">
                  <div><div className="text-white/40 text-xs">Spent</div><div className="font-mono">{selected.spent}</div></div>
                  <div className="h-8 w-px bg-white/[0.06]" />
                  <div><div className="text-white/40 text-xs">Hires</div><div className="font-mono">{selected.hires}</div></div>
                  <div className="h-8 w-px bg-white/[0.06]" />
                  <div><div className="text-white/40 text-xs">Payment</div><div className={selected.verified ? "text-emerald-400" : "text-amber-400"}>{selected.verified ? "Verified" : "Unverified"}</div></div>
                </div>
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-2">Summary</div>
                <p className="text-[14px] leading-relaxed text-white/70">{selected.summary}</p>
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-2">Draft proposal</div>
                <div className="rounded-xl border border-white/[0.06] bg-black/40 p-4 text-[13px] leading-relaxed text-white/80 font-mono">
                  Hi {selected.client.split(" ")[0]},<br /><br />
                  We&apos;ve delivered {selected.tags[0]} projects at similar scale — recently helping a DTC brand cut checkout drop-off by 34% post-migration. Happy to share a 3-week plan + a fixed price within your budget.<br /><br />
                  Base2Brand · b2b.agency
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    data-testid="freelance-submit-proposal"
                    className="rounded-full bg-indigo-500 hover:bg-indigo-400"
                    onClick={() => {
                      toast.success("Proposal queued for review", { description: `Draft submitted for ${selected.client}` });
                      setSelected(null);
                    }}
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" /> One-click submit
                  </Button>
                  <Button variant="ghost" className="text-white/60 rounded-full">
                    <Zap className="h-3.5 w-3.5 mr-1.5" /> Regenerate
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PillFilter({ label, value, options, onChange, testId }) {
  return (
    <div className="flex items-center gap-1 rounded-full border border-white/[0.06] bg-white/[0.02] px-1 py-1" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-wider text-white/40 px-2">{label}</span>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`text-[12px] px-2.5 py-1 rounded-full transition-colors ${
            value === o ? "bg-white/[0.08] text-white" : "text-white/50 hover:text-white"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/50">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-1 font-mono text-lg">{value}</div>
    </div>
  );
}

function JobCard({ job, index, onOpen }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.02 * index, duration: 0.25 }}
      onClick={onOpen}
      data-testid={`freelance-job-${job.id}`}
      className="glass-card p-5 text-left hover:translate-y-[-2px] transition-transform"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="rounded-full bg-white/[0.05] border border-white/[0.08] text-white/70 font-mono text-[10px] uppercase tracking-wider hover:bg-white/[0.05]">
              {job.source}
            </Badge>
            {job.verified && (
              <Badge className="rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/10">
                <ShieldCheck className="h-2.5 w-2.5 mr-1" /> Verified
              </Badge>
            )}
            <span className="flex items-center gap-1 text-[11px] text-white/40 font-mono">
              <Clock className="h-3 w-3" /> {job.postedAgo}
            </span>
          </div>
          <div className="font-display text-lg text-white leading-tight">{job.title}</div>
          <div className="text-[13px] text-white/50 mt-0.5">
            {job.client} · {job.country}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {job.tags.map((t) => (
              <span key={t} className="text-[11px] rounded-full border border-white/[0.06] bg-white/[0.02] px-2 py-0.5 text-white/60">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            <MatchRing value={job.match} />
            <div className="text-right">
              <div className="font-mono text-lg leading-none">{job.match}%</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">match</div>
            </div>
          </div>
          <div className="font-mono text-[13px] text-cyan-300">{job.budget}</div>
          <div className="text-[11px] font-mono text-white/40">Win: {job.winProb}%</div>
        </div>
      </div>
    </motion.button>
  );
}

function MatchRing({ value }) {
  const size = 44;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke="url(#matchGrad)" strokeWidth={stroke} fill="none"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
      />
      <defs>
        <linearGradient id="matchGrad">
          <stop offset="0%" stopColor="#5E6AD2" />
          <stop offset="100%" stopColor="#00E5FF" />
        </linearGradient>
      </defs>
    </svg>
  );
}
